import type { JumpChart } from '@shared/jumpChart';
import type { JumpLayout } from '../jumpPlayfieldGeometry';
import { cubeCanvasX, playfieldYToCanvas } from '../jumpPlayfieldGeometry';
import {
  BEAT_PULSE_STRENGTH,
  COMBO_GLOW_ALPHA_BONUS,
  COMBO_GLOW_RADIUS_BONUS,
  COMBO_TRAIL_ALPHA_BONUS,
  COMBO_TRAIL_WHITEN,
  COMBO_TRAIL_WIDTH_BONUS,
  CUBE_AIR_STRETCH,
  CUBE_AIR_THIN,
  CUBE_JUMP_STRETCH,
  CUBE_JUMP_THIN,
  CUBE_LAND_SQUASH,
  CUBE_LAND_WIDEN,
  CUBE_PRESS_SCALE,
  CUBE_SCALE_MAX,
  CUBE_SCALE_MIN,
} from './jumpRenderConstants';
import type { JumpTrailSample } from './jumpRenderTypes';
import { hexToRgb, hexToRgba } from './jumpRenderColour';
import { drawGlowSprite } from './jumpGlowSprite';

export interface JumpTrailView {
  samples: readonly JumpTrailSample[];
  startIdx: number;
  count: number;
  capacity: number;
}

/**
 * Paint the cube + recent trajectory trail. The cube tumbles (Geometry-Dash
 * style) while airborne and rests flat when grounded — `rotation` is computed
 * by the renderer and passed in. The cube also deforms like a springy mass: a
 * signed `edgeImpulse` squashes it on landing / pops it on launch, and
 * `airStretch` (0..1 of its vertical speed) elongates it along its travel
 * mid-arc. A `pressPulse` (0..1, the eased held-button state) swells it
 * uniformly for press feedback. Skipped entirely once dead — the death-shatter
 * overlay draws the cube's broken pose instead.
 */
export function drawJumpPlayer(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  chart: JumpChart,
  state: { cubeY: number; grounded: boolean; alive: boolean },
  rotation: number,
  trail: JumpTrailView,
  beatPulse: number,
  comboEnergy: number,
  edgeImpulse: number,
  airStretch: number,
  pressPulse: number,
  chromeShadows: boolean,
): void {
  if (!state.alive) {
    return;
  }
  const cx = cubeCanvasX(layout);
  const cy = playfieldYToCanvas(layout, state.cubeY);
  const size = chart.playerSize * layout.scaleY;
  const half = size / 2;
  const palette = chart.palette;
  const pulse = beatPulse * BEAT_PULSE_STRENGTH;

  drawTrail(ctx, layout, trail, cx, palette.player, comboEnergy, chromeShadows);

  // Glow halo — brightens + widens with the beat pulse and the combo streak.
  // A cached glow sprite blitted at the current radius/alpha avoids rebuilding a
  // radial gradient every frame (the cube's centre moves continuously, so the
  // gradient could never be cached by position).
  const glowRadius = half * 2.4 * (1 + pulse * 0.15 + comboEnergy * COMBO_GLOW_RADIUS_BONUS);
  const glowAlpha =
    (state.grounded ? 0.34 + pulse * 0.12 : 0.5 + pulse * 0.2) + comboEnergy * COMBO_GLOW_ALPHA_BONUS;
  if (chromeShadows) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, glowAlpha);
    drawGlowSprite(ctx, palette.player, cx, cy, glowRadius);
    ctx.restore();
  }

  // Springy deformation. A landing splats the cube (compress Y, widen X); a
  // launch pops it (stretch Y, thin X); and the faster it travels mid-arc the
  // more it elongates. The land + jump halves of the signed impulse never fire
  // at once; clampScale is a safety rail for the feel-dial constants. A held
  // button then swells the whole cube uniformly (press feedback) on top.
  const landAmt = Math.max(0, edgeImpulse);
  const jumpAmt = Math.max(0, -edgeImpulse);
  const pressScale = 1 + pressPulse * CUBE_PRESS_SCALE;
  const scaleY =
    clampScale(
      1 - landAmt * CUBE_LAND_SQUASH + jumpAmt * CUBE_JUMP_STRETCH + airStretch * CUBE_AIR_STRETCH,
    ) * pressScale;
  const scaleX =
    clampScale(
      1 + landAmt * CUBE_LAND_WIDEN - jumpAmt * CUBE_JUMP_THIN - airStretch * CUBE_AIR_THIN,
    ) * pressScale;
  // A centre-scaled cube would sink below the floor when it swells or splats;
  // while grounded, offset it so its base stays planted on the surface (it then
  // grows up off the floor). Airborne the arc owns its position, so no offset.
  const groundPlant = state.grounded ? half * (1 - scaleY) : 0;

  ctx.save();
  ctx.translate(cx, cy + groundPlant);
  ctx.rotate(rotation);
  ctx.scale(scaleX, scaleY);
  ctx.fillStyle = palette.player;
  if (chromeShadows) {
    ctx.shadowColor = palette.player;
    ctx.shadowBlur = 12 + pulse * 12 + comboEnergy * 10;
  }
  roundRect(ctx, -half, -half, size, size, Math.max(2, size * 0.16));
  ctx.fill();
  ctx.shadowBlur = 0;

  // Inner face: darker inset square + bright corner so the spin reads clearly.
  ctx.fillStyle = hexToRgba(palette.bg, 0.55);
  const inset = half * 0.42;
  roundRect(ctx, -inset, -inset, inset * 2, inset * 2, Math.max(1, inset * 0.3));
  ctx.fill();
  ctx.fillStyle = hexToRgba('#ffffff', 0.85);
  ctx.fillRect(-half * 0.62, -half * 0.62, half * 0.34, half * 0.34);
  ctx.restore();
}

/** Clamp a composed cube scale factor to the safe deformation band. */
function clampScale(scale: number): number {
  return Math.min(CUBE_SCALE_MAX, Math.max(CUBE_SCALE_MIN, scale));
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function trailSampleAt(view: JumpTrailView, index: number): JumpTrailSample {
  if (view.count < view.capacity) {
    return view.samples[index];
  }
  return view.samples[(view.startIdx + index) % view.capacity];
}

function drawTrail(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  view: JumpTrailView,
  headX: number,
  trailColour: string,
  comboEnergy: number,
  chromeShadows: boolean,
): void {
  if (view.count < 2) {
    return;
  }
  // A long streak thickens the ribbon, lifts its opacity, and shifts it toward
  // white-hot so the cube reads as "charged" the deeper the combo runs.
  const { r, g, b } = whitenTrailRgb(trailColour, comboEnergy * COMBO_TRAIL_WHITEN);
  const headAlpha = Math.min(1, 0.7 + comboEnergy * COMBO_TRAIL_ALPHA_BONUS);
  // The trail's X is a linear function of sample age (`headX - age·54`), so a
  // single horizontal gradient reproduces the per-segment age fade — letting the
  // whole ribbon draw as ONE stroke (one shadow pass) instead of N segments.
  const grad = ctx.createLinearGradient(headX - 54, 0, headX, 0);
  grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
  grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${headAlpha})`);

  ctx.save();
  ctx.lineWidth = 5 + comboEnergy * COMBO_TRAIL_WIDTH_BONUS;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = grad;
  if (chromeShadows) {
    ctx.shadowColor = `rgb(${r}, ${g}, ${b})`;
    ctx.shadowBlur = 10 + comboEnergy * 6;
  }
  ctx.beginPath();
  for (let i = 0; i < view.count; i += 1) {
    const age = (view.count - 1 - i) / view.count;
    const sample = trailSampleAt(view, i);
    const x = headX - age * 54;
    const y = playfieldYToCanvas(layout, sample.cubeY);
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
  ctx.restore();
}

/** Lerp a hex trail colour toward white by `t` (0..1), returning RGB channels. */
function whitenTrailRgb(hex: string, t: number): { r: number; g: number; b: number } {
  const rgb = hexToRgb(hex) ?? { r: 255, g: 255, b: 255 };
  if (t <= 0.01) {
    return rgb;
  }
  return {
    r: Math.round(rgb.r + (255 - rgb.r) * t),
    g: Math.round(rgb.g + (255 - rgb.g) * t),
    b: Math.round(rgb.b + (255 - rgb.b) * t),
  };
}
